import { useState, useCallback, useEffect } from 'react';
import { useToast } from '../stores/toastStore';
import type { ChangeEvent } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  listJournalEntries,
  createJournalEntry,
  updateJournalEntry,
  deleteJournalEntry,
  type JournalEntryListParams,
  type CreateJournalEntryPayload,
  type UpdateJournalEntryPayload,
} from '../services/journalService';
import type { JournalEntry } from '../services/journalService';
import { JournalEntryCard } from '../components/journal/JournalEntryCard';
import { JournalEntryForm } from '../components/journal/JournalEntryForm';
import { JOURNAL_TAGS, journalTagLabel } from '../components/journal/journalTags';
import { ConfirmDialog } from '../components/ui/ConfirmDialog/ConfirmDialog';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { Button } from '../components/atoms/Button/Button';
import { Heading } from '../components/atoms/Typography/Heading';
import { Text } from '../components/atoms/Typography/Text';
import { Input } from '../components/atoms/Input/Input';
import { Select } from '../components/atoms/Select/Select';
import { useTranslation } from '../i18n/useTranslation';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import './JournalOverviewPage.css';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { JournalInsights } from '../components/journalAi/JournalInsights';
import { Alert } from '../components/atoms/Alert/Alert';
import { useOptionalCapture } from '../components/capture/CaptureContext';
import { RecordNoteButton } from '../components/capture/RecordNoteButton';
import { VoiceConsentOffer } from '../components/capture/VoiceConsentOffer';

const PAGE_SIZE = 10;
/** Wait this long after the last keystroke before searching (one request per pause, not per key). */
const SEARCH_DEBOUNCE_MS = 300;

export function JournalOverviewPage() {
  const { t } = useTranslation();
  const { aquariumId } = useParams<{ aquariumId: string }>();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const queue = useOptionalCapture()?.queue ?? [];

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [visibility, setVisibility] = useState<'public' | 'private' | ''>('');
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState<JournalEntry | null>(null);
  const [entryToDelete, setEntryToDelete] = useState<JournalEntry | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  // Search, tag, visibility and paging are applied server-side; `total` /
  // `totalPages` count the filtered set.
  const queryParams: JournalEntryListParams = {
    page,
    limit: PAGE_SIZE,
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(selectedTag ? { tag: selectedTag } : {}),
    ...(visibility ? { visibility } : {}),
  };
  const hasActiveFilters = Boolean(search.trim() || selectedTag || visibility);

  const { data, isLoading, isError, isPlaceholderData, refetch } = useQuery({
    queryKey: ['journalEntries', aquariumId, queryParams],
    queryFn: () => listJournalEntries(aquariumId ?? '', queryParams),
    enabled: Boolean(aquariumId),
    // Keep the current list on screen while the next filter/page loads.
    placeholderData: keepPreviousData,
  });

  const deleteMutation = useMutation({
    mutationFn: (entryId: string) =>
      deleteJournalEntry(aquariumId ?? '', entryId),
    onSuccess: () => {
      setEntryToDelete(null);
      void queryClient.invalidateQueries({
        queryKey: ['journalEntries', aquariumId],
      });
    },
    onError: () => {
      setEntryToDelete(null);
      showToast(t('journal.deleteError'), 'error');
    },
  });

  const handleSearchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value);
      setPage(1);
    },
    [],
  );

  const handleTagChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      setSelectedTag(e.target.value);
      setPage(1);
    },
    [],
  );

  const handleVisibilityChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      setVisibility(e.target.value as 'public' | 'private' | '');
      setPage(1);
    },
    [],
  );

  const handleResetFilters = useCallback(() => {
    setSearch('');
    setDebouncedSearch('');
    setSelectedTag('');
    setVisibility('');
    setPage(1);
  }, []);

  const handleCreateClick = useCallback(() => {
    setEditingEntry(null);
    setShowForm(true);
  }, []);

  // "Write instead" from the capture sheet lands here with ?new=1: open the form once.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('new') !== '1') return;
    handleCreateClick();
    const next = new URLSearchParams(searchParams);
    next.delete('new');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, handleCreateClick]);

  const handleEditClick = useCallback((entry: JournalEntry) => {
    setEditingEntry(entry);
    setShowForm(true);
  }, []);

  const handleDeleteClick = useCallback((entry: JournalEntry) => {
    setEntryToDelete(entry);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (entryToDelete) {
      deleteMutation.mutate(entryToDelete.id);
    }
  }, [deleteMutation, entryToDelete]);

  const handleDeleteCancel = useCallback(() => {
    setEntryToDelete(null);
  }, []);

  const handleFormSubmit = useCallback(
    async (payload: CreateJournalEntryPayload | UpdateJournalEntryPayload) => {
      try {
        const saved = editingEntry
          ? await updateJournalEntry(aquariumId ?? '', editingEntry.id, payload as UpdateJournalEntryPayload)
          : await createJournalEntry(aquariumId ?? '', payload as CreateJournalEntryPayload);
        setShowForm(false);
        setEditingEntry(null);
        void queryClient.invalidateQueries({
          queryKey: ['journalEntries', aquariumId],
        });
        // Follow-up words in the text ("test KH again in 3 days", UC-B): open the entry,
        // where the task detection runs and shows its suggestions.
        if (saved?.taskDetectionSuggested && saved.id) {
          navigate(`/aquariums/${encodeURIComponent(aquariumId ?? '')}/journal/${encodeURIComponent(saved.id)}`, {
            state: { detectTasks: true },
          });
        }
      } catch (err) {
        // Never show raw backend text (CLAUDE.md §9h) — a translated message instead.
        showToast(t('journal.saveError'), 'error');
        throw err;
      }
    },
    [aquariumId, editingEntry, navigate, queryClient, showToast, t],
  );

  const handleFormCancel = useCallback(() => {
    setShowForm(false);
    setEditingEntry(null);
  }, []);

  const entries = data?.entries ?? [];
  const totalPages = data?.totalPages ?? 0;
  const total = data?.total ?? 0;

  // Deleting the last entry of the last page leaves that page empty — step back.
  useEffect(() => {
    if (data && !isPlaceholderData && data.totalPages > 0 && page > data.totalPages) {
      setPage(data.totalPages);
    }
  }, [data, isPlaceholderData, page]);

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent data-testid="journal-overview-page">
          <PageHeader
            title={t('journal.title')}
            description={t('journal.subtitle')}
            actions={
              <>
                <Button variant="primary" onClick={handleCreateClick}>
                  {t('journal.newEntry')}
                </Button>
                {/* Phones record from the Capture tab; the header button is desktop only. */}
                <RecordNoteButton variant="secondary" aquariumId={aquariumId} className="journal-overview-page__record" />
              </>
            }
          />

          <VoiceConsentOffer />
          {queue.length > 0 && (
            <Alert
              tone="info"
              live="polite"
              compact
              title={t('voice.queue.pendingTitle')}
              action={
                <Button variant="secondary" size="sm" onClick={() => navigate('/review')}>
                  {t('voice.queue.view')}
                </Button>
              }
              data-testid="journal-voice-queue"
            >
              {t('voice.queue.pendingBody', { count: queue.length })}
            </Alert>
          )}

          {/* Milestones + on-demand AI aquarium story (audit #7). */}
          <JournalInsights aquariumId={aquariumId ?? ''} />

          {/* Form overlay */}
          {showForm && (
            <div className="journal-overview-page__form-overlay" data-testid="journal-form-overlay">
              <div className="journal-overview-page__form-card">
                <JournalEntryForm
                  aquariumId={aquariumId ?? ''}
                  entry={editingEntry ?? undefined}
                  onSubmit={handleFormSubmit}
                  onCancel={handleFormCancel}
                />
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="journal-overview-page__filters" data-testid="journal-filters">
            <div className="journal-overview-page__search-wrapper">
              <svg className="journal-overview-page__search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <circle cx="6.5" cy="6.5" r="4.5" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M10 10l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <Input
                className="journal-overview-page__search"
                aria-label={t('journal.searchPlaceholder')}
                onChange={handleSearchChange}
                placeholder={t('journal.searchPlaceholder')}
                type="search"
                value={search}
              />
            </div>

            <Select
              className="journal-overview-page__select"
              aria-label={t('journal.filterByTag')}
              onChange={handleTagChange}
              value={selectedTag}
            >
              <option value="">{t('journal.allTags')}</option>
              {JOURNAL_TAGS.map((tag) => (
                <option key={tag} value={tag}>{journalTagLabel(tag, t)}</option>
              ))}
            </Select>

            <Select
              className="journal-overview-page__select"
              aria-label={t('journal.filterByVisibility')}
              onChange={handleVisibilityChange}
              value={visibility}
            >
              <option value="">{t('journal.allVisibility')}</option>
              <option value="public">{t('journal.shared')}</option>
              <option value="private">{t('journal.personal')}</option>
            </Select>

            {hasActiveFilters && (
              <Button variant="ghost" onClick={handleResetFilters} data-testid="journal-reset-filters">
                {t('journal.resetFilters')}
              </Button>
            )}
          </div>

          {/* Announces the filtered result count to screen readers. */}
          <LiveRegion visuallyHidden busy={isLoading}>
            {hasActiveFilters && data && !isError ? t('journal.resultCount', { count: total }) : ''}
          </LiveRegion>

          {/* States */}
          {isLoading && (
            <div className="journal-overview-page__loading" data-testid="journal-loading">
              <div className="journal-overview-page__skeleton" />
              <div className="journal-overview-page__skeleton" />
              <div className="journal-overview-page__skeleton" />
            </div>
          )}

          {isError && (
            <LoadErrorState
              data-testid="journal-error"
              title={t('journal.error')}
              onRetry={() => void refetch()}
            />
          )}

          {!isLoading && !isError && entries.length === 0 && hasActiveFilters && (
            <div className="journal-overview-page__empty" data-testid="journal-empty-filtered">
              <div className="journal-overview-page__empty-icon" aria-hidden="true">🔍</div>
              <Heading level={2} className="journal-overview-page__empty-title">
                {t('journal.filteredEmptyTitle')}
              </Heading>
              <Text className="journal-overview-page__empty-subtitle">
                {t('journal.filteredEmptySubtitle')}
              </Text>
              <Button variant="secondary" onClick={handleResetFilters}>
                {t('journal.resetFilters')}
              </Button>
            </div>
          )}

          {!isLoading && !isError && entries.length === 0 && !hasActiveFilters && (
            <div className="journal-overview-page__empty" data-testid="journal-empty">
              <div className="journal-overview-page__empty-icon" aria-hidden="true">📖</div>
              <Heading level={2} className="journal-overview-page__empty-title">
                {t('journal.emptyTitle')}
              </Heading>
              <Text className="journal-overview-page__empty-subtitle">
                {t('journal.emptySubtitle')}
              </Text>
              <Button variant="secondary" onClick={handleCreateClick}>
                {t('journal.newEntry')}
              </Button>
            </div>
          )}

          {!isLoading && !isError && entries.length > 0 && (
            <ul className="journal-overview-page__entry-list" data-testid="journal-entry-list">
              {entries.map((entry) => (
                <li key={entry.id} className="journal-overview-page__entry-item">
                  <JournalEntryCard
                    entry={entry}
                    onDelete={handleDeleteClick}
                    onEdit={handleEditClick}
                  />
                </li>
              ))}
            </ul>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <nav className="journal-overview-page__pagination" aria-label={t('journal.paginationLabel')} data-testid="journal-pagination">
              <Button
                variant="ghost"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                {t('journal.previous')}
              </Button>
              <span className="journal-overview-page__page-indicator">
                {t('journal.pageOf', { page, total: totalPages })}
              </span>
              <Button
                variant="ghost"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                {t('journal.next')}
              </Button>
            </nav>
          )}

          <ConfirmDialog
            isOpen={entryToDelete !== null}
            title={t('journal.deleteDialog.title')}
            message={t('journal.deleteDialog.message', { title: entryToDelete?.title ?? '' })}
            confirmLabel={t('journal.deleteDialog.confirm')}
            cancelLabel={t('journal.deleteDialog.cancel')}
            onConfirm={handleDeleteConfirm}
            onCancel={handleDeleteCancel}
            isConfirming={deleteMutation.isPending}
          />
        </PageContent>
      }
    />
  );
}
